import { X, ChevronRight } from 'lucide-react'
import { officeConfirmed, otherOffices, officeMilesText, officeDirectionsUrl } from '../../utils/officeAddress' // s110
import { clinicianName, clinicianTo } from '../../utils/clinicianName.js'

/**
 * MapPinCard — s104 (TestFlight b70, Dr. Rami: "Clicking a non orella pin does not indicate who the
 * selected doctor is. Should open their listing when clicked."; his pick: a card over the map).
 *
 * The listing of the pin the patient tapped, laid over the bottom of the share picker's map. ONE card
 * for both kinds of pin, so the two cannot drift:
 *   - a directory doctor (not on Orella): name, specialty · city, fax badge, distance, and the same
 *     action as that doctor's row in "Other doctors near you" (send the Case Report);
 *   - an Orella doctor: name, specialty, "On Orella", distance, and the same action as the Orella
 *     list's card (choose this doctor), plus View Profile.
 * s106 adds one key (doctors.sendCaseToOffice). Otherwise it carries NO new copy: every label is an existing key (sendReport.send, share.select,
 * share.viewProfile, doctors.showOrella, findDoctor.faxOnFile / noFax / miles, common.close).
 * Presentational: the caller owns what a tap does.
 */
export default function MapPinCard({ kind, doc, miles, onAction, onViewProfile, onClose, t, links = false, onSend = null }) {
  // ★ s106 (TestFlight b72, Dr. Rami: "Create an option to send a case ... Needs to work for the docs not
  //   current subscribed to orella"; mockup "Orella Doctors Tab Send"): on the Doctors tab a directory
  //   card leads with "Send my case to this office" (`onSend`: StartCaseSheet with the office saved),
  //   fax on file or not; Call office and Get Directions stay under it.
  // ★ s105 (TestFlight b71, "update this with the new map search modal with all NPPES data"): on the
  //   Doctors tab there is no case to send, so `links` turns a directory card's actions into Call office
  //   (tel:) and Get Directions (maps), and an Orella card's into View Profile only. Existing copy only.
  if (!doc) return null
  const dir = kind === 'directory'
  const name = dir
    ? `${doc.name || ''}${doc.credential ? `, ${doc.credential}` : ''}`
    : (() => {
        return clinicianName(doc, 'formal')
      })()
  const initials = dir
    ? initialsOf(doc.name)
    : `${doc.first_name?.[0] || ''}${doc.last_name?.[0] || ''}`.toUpperCase()
  const sub = dir
    ? [doc.specialty, [doc.address?.city, doc.address?.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')
    : (doc.matchedSpecialty || doc.specialties?.[0] || doc.specialty || '')
  const m = typeof miles === 'number' && isFinite(miles) ? Math.round(miles * 10) / 10 : null
  // s110: an address we only know the area of: approximate miles, no directions, and the card says why.
  const confirmed = !dir || officeConfirmed(doc)
  const directions = dir ? officeDirectionsUrl(doc) : null
  return (
    <div data-testid="map-pin-card" data-kind={dir ? 'directory' : 'orella'}
         className="absolute left-2.5 right-2.5 bottom-2.5 rounded-2xl bg-white shadow-lg p-3 flex flex-col gap-2.5"
         style={{ border: '1.5px solid #46A8F9', zIndex: 5 }}>
      <div className="flex gap-3 items-start">
        <span className="w-[40px] h-[40px] rounded-full grid place-items-center flex-none text-[13px] font-extrabold"
              style={dir ? { background: '#DCEBF7', color: '#15354F' } : { background: '#15354F', color: '#ffffff' }}>
          {initials}
        </span>
        <span className="flex-1 min-w-0">
          <b className="block text-[14px] font-bold leading-snug" style={{ color: '#15354F' }}>{name}</b>
          {sub && <small className="block text-[12px] leading-snug" style={{ color: '#5A7185' }}>{sub}</small>}
          {dir && otherOffices(doc) > 0 && (
            <small className="block text-[12px] font-bold leading-snug" style={{ color: '#2F7FC9' }}>
              {t('findDoctor.officesCount', { defaultValue: '{{n}} offices · nearest shown', n: otherOffices(doc) })}
            </small>
          )}
          <span className="flex items-center gap-2 mt-1 flex-wrap">
            {dir ? (
              <span className="inline-block text-[11px] font-bold rounded-full px-2 py-0.5"
                    style={doc.faxOnFile ? { color: '#5A7185', background: '#F2F6FA' } : { color: '#B45309', background: '#FFF7E8' }}>
                {doc.faxOnFile ? t('findDoctor.faxOnFile', 'Fax on file') : t('findDoctor.noFax', 'No fax listed')}
              </span>
            ) : (
              <span className="inline-block text-[11px] font-bold rounded-full px-2 py-0.5" style={{ color: '#15354F', background: '#E8F4FE' }}>
                {t('doctors.showOrella', 'On Orella')}
              </span>
            )}
            {dir && !confirmed && (
              <span className="inline-block text-[11px] font-bold rounded-full px-2 py-0.5" style={{ color: '#B45309', background: '#FFF7E8' }}>
                {t('findDoctor.addressUnconfirmed', 'Address not confirmed')}
              </span>
            )}
            {m !== null && <span className="text-[12px] font-bold" style={{ color: '#93A5B4' }}>{officeMilesText({ ...doc, miles: m, addressConfirmed: confirmed }, t)}</span>}
          </span>
        </span>
        <button type="button" onClick={onClose} aria-label={t('common.close', 'Close')} data-cta="map-pin-card-close"
                className="w-8 h-8 -mr-1 -mt-1 rounded-full grid place-items-center flex-none" style={{ color: '#93A5B4' }}>
          <X size={16} />
        </button>
      </div>
      {dir && !confirmed && (
        <p className="text-[12.5px] leading-snug" style={{ color: '#3E5568' }} data-testid="map-pin-card-area-note">
          {t('findDoctor.areaOnlyNote', "We only know this office's area, not its street address. Call the office to check before you go.")}
        </p>
      )}
      {links ? (
      <div className="flex flex-col gap-2">
      {dir && onSend && (
        <button type="button" onClick={onSend} data-cta="map-pin-card-send-case"
                className="w-full py-2.5 rounded-full text-white text-[13.5px] font-bold flex items-center justify-center gap-1.5 active:opacity-90"
                style={{ backgroundColor: '#46A8F9' }}>
          {/* s120 (Dr. Rami 2026-10-28): the button names the clinician ("Change to doctor. Then they select the
              specific office if needed"); NPs/PAs are named, never called "doctor" (D530). */}
          {clinicianTo(doc)
            ? t('doctors.sendCaseToClinician', { defaultValue: 'Send my case to {{clinician}}', clinician: clinicianTo(doc) })
            : t('doctors.sendCaseToProvider', 'Send my case to this clinician')}
          <ChevronRight size={15} />
        </button>
      )}
      <div className="flex gap-2">
        {dir && doc.phone && (
          <a href={`tel:${String(doc.phone).replace(/[^\d+]/g, '')}`} data-cta="map-pin-card-call"
             className="flex-1 py-2.5 rounded-full text-white text-[13.5px] font-bold flex items-center justify-center gap-1.5"
             style={{ backgroundColor: '#15354F' }}>
            {t('doctorProfile.callOffice', 'Call office')}
          </a>
        )}
        {dir && directions && (
          <a href={directions}
             target="_blank" rel="noopener noreferrer" data-cta="map-pin-card-directions"
             className="flex-1 py-2.5 rounded-full text-[13px] font-semibold bg-white flex items-center justify-center"
             style={{ color: '#15354F', border: '1.5px solid #D8E6F0' }}>
            {t('visits.getDirections', 'Get Directions')}
          </a>
        )}
        {!dir && onViewProfile && (
          <button type="button" onClick={onViewProfile} data-cta="map-pin-card-profile"
                  className="flex-1 py-2.5 rounded-full text-white text-[13.5px] font-bold flex items-center justify-center gap-1.5"
                  style={{ backgroundColor: '#15354F' }}>
            {t('share.viewProfile', 'View Profile')}
            <ChevronRight size={15} />
          </button>
        )}
      </div>
      </div>
      ) : (
      <div className="flex gap-2">
        {!dir && onViewProfile && (
          <button type="button" onClick={onViewProfile} data-cta="map-pin-card-profile"
                  className="px-3.5 py-2.5 rounded-full text-[13px] font-semibold bg-white flex-none"
                  style={{ color: '#15354F', border: '1.5px solid #D8E6F0' }}>
            {t('share.viewProfile', 'View Profile')}
          </button>
        )}
        <button type="button" onClick={onAction} data-cta="map-pin-card-action"
                className="flex-1 py-2.5 rounded-full text-white text-[13.5px] font-bold flex items-center justify-center gap-1.5 active:opacity-90"
                style={{ backgroundColor: '#15354F' }}>
          {dir ? t('sendReport.send', 'Send my Case Report') : t('share.select', 'Select')}
          <ChevronRight size={15} />
        </button>
      </div>
      )}
    </div>
  )
}

function initialsOf(name) {
  const parts = String(name || '').replace(/^dr\.?\s+/i, '').split(/\s+/).filter(Boolean)
  return ((parts[0] || '')[0] || '').toUpperCase() + ((parts[parts.length - 1] || '')[0] || '').toUpperCase()
}
